/**
 * Product Main Custom Section JavaScript
 * Handles image grid, lightbox gallery, mobile thumbnails, quantity selector, and touch interactions
 */

(function() {
  'use strict';

  function initProductMain() {
    const sections = /** @type {NodeListOf<HTMLElement>} */ (document.querySelectorAll('.product-main'));
    sections.forEach(initSection);
  }

  /** @param {HTMLElement} section */
  function initSection(section) {
    const mainImage = /** @type {HTMLImageElement | null} */ (section.querySelector('.product-main__image'));
    const thumbnailButtons = /** @type {NodeListOf<HTMLElement>} */ (section.querySelectorAll('[data-thumbnail]'));
    const qtyInput = /** @type {HTMLInputElement | null} */ (section.querySelector('.product-main__qty-input'));
    const qtyDecrease = /** @type {HTMLButtonElement | null} */ (section.querySelector('[data-qty-decrease]'));
    const qtyIncrease = /** @type {HTMLButtonElement | null} */ (section.querySelector('[data-qty-increase]'));
    const scrollTargets = /** @type {NodeListOf<HTMLElement>} */ (section.querySelectorAll('[data-scroll-target]'));
    const productForm = /** @type {HTMLElement | null} */ (
      section.querySelector('product-form-component[data-membership-product-id]')
    );
    const membershipOption = /** @type {HTMLElement | null} */ (
      section.querySelector('.product-main__membership-option')
    );
    const membershipCheckbox = /** @type {HTMLInputElement | null} */ (
      membershipOption?.querySelector('[data-membership-checkbox]') || null
    );

    if (productForm && membershipOption && membershipCheckbox && section.dataset.membershipSyncInitialized !== 'true') {
      section.dataset.membershipSyncInitialized = 'true';

      /** @param {boolean} membershipInCart */
      const setMembershipInCart = (membershipInCart) => {
        membershipCheckbox.checked = false;
        membershipOption.hidden = membershipInCart;
        productForm.dataset.membershipAddOn = membershipInCart ? 'false' : 'true';
      };

      document.addEventListener('cart:update', (event) => {
        if (!(event instanceof CustomEvent)) return;
        const items = event.detail?.resource?.items;
        if (event.detail?.data?.variantId === productForm.dataset.membershipVariantId) {
          setMembershipInCart(true);
        } else if (Array.isArray(items)) {
          setMembershipInCart(items.some(
            /** @param {{ product_id?: number | string }} item */
            (item) => item.product_id?.toString() === productForm.dataset.membershipProductId
          ));
        }
      });
    }

    // Mobile thumbnail click handling
    if (thumbnailButtons.length > 0 && mainImage) {
      thumbnailButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          const mediaSrc = btn.dataset.mediaSrc;
          if (mediaSrc) {
            mainImage.src = mediaSrc;
            thumbnailButtons.forEach(t => t.classList.remove('is-active'));
            btn.classList.add('is-active');
            // Sync lightbox index on mobile main image
            if (btn.dataset.mediaIndex !== undefined) {
              mainImage.dataset.mediaIndex = btn.dataset.mediaIndex;
            }
          }
        });
      });

      // Add swipe navigation to mobile main image
      const thumbnailSources = /** @type {string[]} */ ([]);
      thumbnailButtons.forEach(btn => {
        thumbnailSources.push(btn.dataset.mediaSrc || '');
      });

      // Preload all mobile swipe images
      thumbnailSources.forEach(src => {
        if (src) {
          const img = new Image();
          img.src = src;
        }
      });

      let currentThumbnailIndex = 0;

      function goToNextThumbnail() {
        currentThumbnailIndex = (currentThumbnailIndex + 1) % thumbnailSources.length;
        updateMainImageFromIndex();
      }

      function goToPrevThumbnail() {
        currentThumbnailIndex = (currentThumbnailIndex - 1 + thumbnailSources.length) % thumbnailSources.length;
        updateMainImageFromIndex();
      }

      function updateMainImageFromIndex() {
        if (thumbnailSources[currentThumbnailIndex]) {
          mainImage.src = thumbnailSources[currentThumbnailIndex];
          thumbnailButtons.forEach((btn, i) => {
            btn.classList.toggle('is-active', i === currentThumbnailIndex);
            if (i === currentThumbnailIndex && btn.dataset.mediaIndex !== undefined) {
              mainImage.dataset.mediaIndex = btn.dataset.mediaIndex;
            }
          });
        }
      }

      initSwipeNavigation(mainImage, goToNextThumbnail, goToPrevThumbnail);
    }

    // Initialize mobile thumbnail wrappers
    const thumbnailWrappers = /** @type {NodeListOf<HTMLElement>} */ (
      section.querySelectorAll('.product-main__thumbnails-wrapper')
    );
    thumbnailWrappers.forEach(wrapper => {
      initThumbnailWrapper(wrapper);
    });

    // Lightbox
    initLightbox(section);

    // Quantity selector
    if (qtyInput && qtyDecrease && qtyIncrease) {
      qtyDecrease.addEventListener('click', () => {
        const currentValue = parseInt(qtyInput.value, 10) || 1;
        if (currentValue > 1) {
          qtyInput.value = (currentValue - 1).toString();
          qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });

      qtyIncrease.addEventListener('click', () => {
        const currentValue = parseInt(qtyInput.value, 10) || 1;
        qtyInput.value = (currentValue + 1).toString();
        qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      });

      qtyInput.addEventListener('change', () => {
        const value = parseInt(qtyInput.value, 10);
        if (isNaN(value) || value < 1) {
          qtyInput.value = '1';
        }
      });
    }

    // Dynamic sticky: make the shorter column sticky
    const gallery = section.querySelector('.product-main__gallery');
    const details = section.querySelector('.product-main__details');
    if (gallery && details) {
      function updateStickyColumn() {
        // Only apply on desktop
        if (window.innerWidth <= 768) {
          gallery.classList.remove('is-sticky');
          details.classList.remove('is-sticky');
          return;
        }

        const galleryHeight = gallery.scrollHeight;
        const detailsHeight = details.scrollHeight;

        if (galleryHeight <= detailsHeight) {
          gallery.classList.add('is-sticky');
          details.classList.remove('is-sticky');
        } else {
          details.classList.add('is-sticky');
          gallery.classList.remove('is-sticky');
        }
      }

      updateStickyColumn();
      window.addEventListener('resize', updateStickyColumn);
    }

    // Smooth scroll to target sections
    if (scrollTargets.length) {
      scrollTargets.forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const targetSelector = btn.getAttribute('data-scroll-target');
          if (!targetSelector) return;
          const targetEl = document.querySelector(targetSelector);
          if (targetEl && typeof targetEl.scrollIntoView === 'function') {
            targetEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else if (targetSelector.startsWith('#')) {
            window.location.hash = targetSelector;
          }
        });
      });
    }
  }

  /**
   * Initialize lightbox gallery
   */
  /** @param {HTMLElement} section */
  function initLightbox(section) {
    const sectionRoot = section.closest('.product-main-section');
    const lightbox = /** @type {HTMLElement | null} */ (
      (sectionRoot || section).querySelector('[data-lightbox]')
    );

    if (!lightbox) return;

    const lightboxImage = /** @type {HTMLImageElement | null} */ (lightbox.querySelector('[data-lightbox-image]'));
    const lightboxThumbs = /** @type {NodeListOf<HTMLElement>} */ (lightbox.querySelectorAll('[data-lightbox-thumb]'));
    const prevBtn = /** @type {HTMLButtonElement | null} */ (lightbox.querySelector('[data-lightbox-prev]'));
    const nextBtn = /** @type {HTMLButtonElement | null} */ (lightbox.querySelector('[data-lightbox-next]'));
    const closeButtons = /** @type {NodeListOf<HTMLElement>} */ (lightbox.querySelectorAll('[data-lightbox-close]'));
    const openers = /** @type {NodeListOf<HTMLElement>} */ (section.querySelectorAll('[data-lightbox-open]'));

    // Build sources array from lightbox thumbs
    const sources = /** @type {string[]} */ ([]);
    lightboxThumbs.forEach(thumb => {
      sources.push(thumb.dataset.thumbSrc || '');
    });

    if (sources.length === 0) return;

    // Preload all full-size lightbox images
    sources.forEach(src => {
      if (src) {
        const img = new Image();
        img.src = src;
      }
    });

    let currentIndex = 0;

    /** @param {number} index */
    function openLightbox(index) {
      currentIndex = index;
      updateLightboxImage();
      lightbox.classList.add('is-open');
      lightbox.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      lightbox.classList.remove('is-open');
      lightbox.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    function updateLightboxImage() {
      if (!lightboxImage) return;
      lightboxImage.src = sources[currentIndex] || '';
      lightboxImage.alt = 'Product image ' + (currentIndex + 1);

      lightboxThumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === currentIndex);
      });
    }

    function goNext() {
      currentIndex = (currentIndex + 1) % sources.length;
      updateLightboxImage();
    }

    function goPrev() {
      currentIndex = (currentIndex - 1 + sources.length) % sources.length;
      updateLightboxImage();
    }

    // Open from grid items or mobile image
    openers.forEach(opener => {
      opener.addEventListener('click', (e) => {
        e.preventDefault();
        const index = parseInt(opener.dataset.mediaIndex, 10) || 0;
        openLightbox(index);
      });
    });

    // Close buttons (X and backdrop)
    closeButtons.forEach(btn => {
      btn.addEventListener('click', closeLightbox);
    });

    // Arrows
    if (prevBtn) prevBtn.addEventListener('click', goPrev);
    if (nextBtn) nextBtn.addEventListener('click', goNext);

    // Lightbox thumbs
    lightboxThumbs.forEach(thumb => {
      thumb.addEventListener('click', () => {
        const index = parseInt(thumb.dataset.thumbIndex, 10) || 0;
        currentIndex = index;
        updateLightboxImage();
      });
    });

    // Keyboard navigation
    document.addEventListener('keydown', (e) => {
      if (!lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
    });
  }

  /**
   * Initialize a single thumbnail wrapper with arrows and scrolling
   */
  /** @param {HTMLElement} wrapper */
  function initThumbnailWrapper(wrapper) {
    const thumbnails = /** @type {HTMLElement | null} */ (wrapper.querySelector('[data-thumbnails]'));
    const prevBtn = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('[data-thumb-prev]'));
    const nextBtn = /** @type {HTMLButtonElement | null} */ (wrapper.querySelector('[data-thumb-next]'));

    if (!thumbnails || !prevBtn || !nextBtn) return;

    const scrollAmount = 138;

    prevBtn.addEventListener('click', () => {
      thumbnails.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    });

    nextBtn.addEventListener('click', () => {
      thumbnails.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    });

    function updateArrowStates() {
      const scrollLeft = thumbnails.scrollLeft;
      const maxScroll = thumbnails.scrollWidth - thumbnails.clientWidth;
      prevBtn.disabled = scrollLeft <= 0;
      nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }

    thumbnails.addEventListener('scroll', updateArrowStates);
    updateArrowStates();

    initTouchScroll(thumbnails);
  }

  /**
   * Initialize swipe navigation for an element
   */
  /**
   * @param {HTMLElement} element
   * @param {() => void} onNext
   * @param {() => void} onPrev
   */
  function initSwipeNavigation(element, onNext, onPrev) {
    let touchStartX = 0;
    let touchEndX = 0;
    const minSwipeDistance = 50;

    element.addEventListener('touchstart', (e) => {
      touchStartX = e.changedTouches[0].screenX;
    }, { passive: true });

    element.addEventListener('touchend', (e) => {
      touchEndX = e.changedTouches[0].screenX;
      handleSwipe();
    }, { passive: true });

    function handleSwipe() {
      const diff = touchStartX - touchEndX;
      if (Math.abs(diff) < minSwipeDistance) return;

      if (diff > 0) {
        // Swipe left - go next
        onNext();
      } else {
        // Swipe right - go prev
        onPrev();
      }
    }
  }

  /**
   * Initialize touch/drag scrolling for an element
   */
  /** @param {HTMLElement} element */
  function initTouchScroll(element) {
    let isDown = false;
    let startX = 0;
    let scrollLeft = 0;

    element.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;
      isDown = true;
      element.style.cursor = 'grabbing';
      startX = e.pageX - element.offsetLeft;
      scrollLeft = element.scrollLeft;
    });

    element.addEventListener('mouseleave', () => {
      isDown = false;
      element.style.cursor = '';
    });

    element.addEventListener('mouseup', () => {
      isDown = false;
      element.style.cursor = '';
    });

    element.addEventListener('mousemove', (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - element.offsetLeft;
      const walk = (x - startX) * 1.5;
      element.scrollLeft = scrollLeft - walk;
    });

    let touchStartX = 0;
    let touchScrollLeft = 0;

    element.addEventListener('touchstart', (e) => {
      touchStartX = e.touches[0].pageX - element.offsetLeft;
      touchScrollLeft = element.scrollLeft;
    }, { passive: true });

    element.addEventListener('touchmove', (e) => {
      if (!touchStartX) return;
      const x = e.touches[0].pageX - element.offsetLeft;
      const walk = (x - touchStartX) * 1.5;
      element.scrollLeft = touchScrollLeft - walk;
    }, { passive: true });

    element.addEventListener('touchend', () => {
      touchStartX = 0;
    }, { passive: true });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductMain);
  } else {
    initProductMain();
  }

  // Re-initialize on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', (e) => {
    if (!(e.target instanceof HTMLElement)) return;
    const section = /** @type {HTMLElement | null} */ (e.target.querySelector('.product-main'));
    if (section) initSection(section);
  });
})();
